import {
  RaceWorld,
  Track,
  RaceMode,
  InputFrame,
  emptyInput,
  TICK_DT,
  TICK_RATE,
  COUNTDOWN_TICKS,
  RaceShipInfo,
  BotDifficulty,
  Vec3,
  Quat,
  GhostData,
  GHOST_VERSION,
  encodeGhostFrames,
  readGhostFrame,
  ghostFrameCount,
  ticksToTimeString,
} from '@voidline/shared';
import { GameDriver, RaceView, ShipView, makeShipView, GameEvents } from './view.js';
import { InputManager } from '../input.js';
import { Settings } from '../persist.js';

export interface LocalGameConfig {
  track: Track;
  trackId: string;
  trackSeed: string | null;
  mode: RaceMode;
  laps: number;
  playerVehicle: string;
  playerPaint: string;
  playerName: string;
  botCount: number;
  botDifficulty: BotDifficulty;
  eliminationInterval?: number;
  ghost?: GhostData | null;
  events?: GameEvents;
  botVehiclePool?: string[];
}

const BOT_NAMES = ['VEKTOR', 'HALCYON-9', 'SABLE', 'MIRAGE', 'KILOWATT', 'NOMAD', 'PALEFIRE', 'RIFT'];

/**
 * Offline play: runs the full authoritative simulation in-process (same code the
 * server runs), with countdown, bots, ghosts, and per-tick event callbacks.
 */
export class LocalGame implements GameDriver {
  readonly view: RaceView;
  private world: RaceWorld;
  private accumulator = 0;
  private countdownLeft = COUNTDOWN_TICKS;
  private phase: 'countdown' | 'racing' | 'finished' = 'countdown';
  private input: InputFrame = emptyInput();
  private paused = false;
  private events?: GameEvents;
  private lastBeep = 4;

  // interpolation state: previous & current tick poses
  private prevPos: Vec3[] = [];
  private prevQuat: Quat[] = [];
  private curPos: Vec3[] = [];
  private curQuat: Quat[] = [];
  private scratchQuat = new Quat();
  private scratchRight = new Vec3();

  // ghost
  private ghost: GhostData | null = null;
  private ghostSamples: { pos: Vec3; quat: Quat }[] = [];
  private recording = true;

  // split tracking
  private lastSplitDelta: number | null = null;
  private bestSplits: number[] = [];
  private curSplits: number[] = [];

  constructor(
    private cfg: LocalGameConfig,
    private inputMgr: InputManager,
    private settings: Settings
  ) {
    this.events = cfg.events;
    const ships: { vehicleId: string; isBot: boolean; botDifficulty?: BotDifficulty; botSeed?: number }[] = [
      { vehicleId: cfg.playerVehicle, isBot: false },
    ];
    const infos: RaceShipInfo[] = [
      {
        shipIndex: 0,
        playerId: 'local',
        name: cfg.playerName || 'YOU',
        vehicleId: cfg.playerVehicle,
        paintId: cfg.playerPaint,
        isBot: false,
      },
    ];
    const pool = cfg.botVehiclePool ?? ['kestrel', 'rampart', 'nix7', 'sableline', 'aurelion', 'pyrefalcon', 'glacio'];
    const botCount = cfg.mode === 'timetrial' ? 0 : Math.min(7, cfg.botCount);
    for (let i = 0; i < botCount; i++) {
      const vehicleId = pool[i % pool.length];
      ships.push({ vehicleId, isBot: true, botDifficulty: cfg.botDifficulty, botSeed: i * 1013 + 7 });
      infos.push({
        shipIndex: i + 1,
        playerId: null,
        name: BOT_NAMES[i % BOT_NAMES.length],
        vehicleId,
        paintId: 'bot',
        isBot: true,
      });
    }

    this.world = new RaceWorld({
      track: cfg.track,
      mode: cfg.mode,
      laps: cfg.laps,
      ships,
      eliminationInterval: cfg.eliminationInterval,
    });

    this.world.setEvents({
      onCheckpoint: (i) => {
        if (i === 0) this.trackSplit();
        this.events?.onCheckpoint?.(i === 0);
        if (i === 0) return;
      },
      onLap: (i, lap, lapTicks) => {
        if (i === 0) {
          const best = this.world.progress[0].bestLapTicks === lapTicks;
          this.events?.onLapComplete?.(true, lapTicks, best);
          this.bestSplits = best ? [...this.curSplits] : this.bestSplits;
          this.curSplits = [];
        }
      },
      onFinish: (i) => {
        const placement = this.world.standings.indexOf(i) + 1;
        // The award handler reads this.view (finished/placement/lap times), but
        // this event fires mid-step, before update() rebuilds the view — sync it
        // first or the handler sees last frame's finished=false and skips XP.
        if (i === 0) this.buildView();
        this.events?.onFinish?.(i === 0, placement);
      },
      onEliminated: (i) => this.events?.onEliminated?.(i, i === 0),
      onWallHit: (ship, intensity) => this.events?.onWallHit?.(intensity, ship.index === 0),
      onShipHit: (a, b, intensity) =>
        this.events?.onShipHit?.(intensity, a.index === 0 || b.index === 0),
      onBoostPad: (ship) => this.events?.onBoostPad?.(ship.index === 0),
      onLand: (ship, intensity) => this.events?.onLand?.(intensity, ship.index === 0),
      onRespawnStart: (ship) => this.events?.onRespawn?.(ship.index === 0),
      onFallOff: (ship) => this.events?.onFallOff?.(ship.index === 0),
    });

    // interpolation buffers
    for (let i = 0; i < ships.length; i++) {
      this.prevPos.push(new Vec3());
      this.prevQuat.push(new Quat());
      this.curPos.push(new Vec3());
      this.curQuat.push(new Quat());
    }
    this.snapshotPoses();
    this.snapshotPoses(); // fill prev too

    this.ghost = cfg.ghost ?? null;

    this.view = {
      track: cfg.track,
      mode: cfg.mode,
      laps: this.world.laps,
      phase: 'countdown',
      countdownSeconds: COUNTDOWN_TICKS / TICK_RATE,
      raceTicks: 0,
      ships: infos.map(makeShipView),
      standings: [...this.world.standings],
      localIndex: 0,
      local: null,
      ghost: this.ghost ? { pos: new Vec3(), quat: new Quat(), visible: true } : null,
      net: null,
      eliminationIn: null,
    };
  }

  private trackSplit(): void {
    const prog = this.world.progress[0];
    const cpIdx = this.curSplits.length;
    this.curSplits.push(this.world.tick - prog.lapStartTick);
    if (this.bestSplits[cpIdx] !== undefined) {
      this.lastSplitDelta = ((this.curSplits[cpIdx] - this.bestSplits[cpIdx]) / TICK_RATE) * 1000;
    }
  }

  private snapshotPoses(): void {
    for (let i = 0; i < this.world.ships.length; i++) {
      this.prevPos[i].copy(this.curPos[i]);
      this.prevQuat[i].copy(this.curQuat[i]);
      const ship = this.world.ships[i];
      this.curPos[i].copy(ship.pos);
      ship.basis(this.scratchQuat, this.scratchRight);
      this.curQuat[i].copy(this.scratchQuat);
    }
  }

  setPaused(p: boolean): void {
    this.paused = p;
  }

  update(dt: number): void {
    if (this.paused) return;
    this.accumulator += Math.min(dt, 0.25);

    while (this.accumulator >= TICK_DT) {
      this.accumulator -= TICK_DT;
      this.stepTick();
    }
    this.buildView();
  }

  private stepTick(): void {
    if (this.phase === 'countdown') {
      this.countdownLeft--;
      const secs = Math.ceil(this.countdownLeft / TICK_RATE);
      if (secs < this.lastBeep && secs > 0) {
        this.lastBeep = secs;
        this.events?.onCountdownBeep?.(secs);
      }
      this.snapshotPoses();
      this.world.step(); // settle on hover springs
      if (this.countdownLeft <= 0) {
        this.phase = 'racing';
        this.world.tick = 0;
        for (const prog of this.world.progress) prog.lapStartTick = 0;
        this.events?.onGo?.();
      }
      return;
    }
    if (this.phase === 'finished') return;

    // sample live input at tick rate
    this.inputMgr.sample(TICK_DT, this.input);
    this.applyAssists(this.input);
    this.world.setInput(0, this.input);
    this.snapshotPoses();
    this.world.step();

    // ghost recording (every 3 ticks = 20Hz)
    if (this.recording && this.world.tick % 3 === 0 && !this.world.progress[0].finished) {
      const ship = this.world.ships[0];
      ship.basis(this.scratchQuat, this.scratchRight);
      this.ghostSamples.push({ pos: ship.pos.clone(), quat: this.scratchQuat.clone() });
      // cap ~10 min of recording
      if (this.ghostSamples.length > 12000) this.recording = false;
    }

    if (this.world.raceOver) {
      this.phase = 'finished';
      this.events?.onRaceEnd?.();
    }
  }

  private applyAssists(input: InputFrame): void {
    const a = this.settings.assists;
    const ship = this.world.ships[0];
    if (a.steeringHelp && ship.grounded && Math.abs(input.steer) < 0.05) {
      // gentle magnetic pull toward the road center when coasting straight
      const target = -ship.lateral / (ship.trackWidth * 0.5);
      input.steer = Math.max(-0.25, Math.min(0.25, target * 0.35));
    }
    if (a.brakeHelp && ship.grounded) {
      const line = this.cfg.track.racingLine;
      const sp = this.cfg.track.main;
      const look = Math.max(20, ship.speed() * 0.7);
      const li = Math.floor(sp.wrapS(ship.s + look) / sp.spacing) % line.speed.length;
      if (ship.speed() > line.speed[li] * 1.12) {
        input.brakeL = Math.max(input.brakeL, 0.5);
        input.brakeR = Math.max(input.brakeR, 0.5);
      }
    }
  }

  private buildView(): void {
    const v = this.view;
    const alpha = this.accumulator / TICK_DT;
    v.phase = this.phase === 'finished' ? 'finished' : this.phase;
    v.countdownSeconds = Math.max(0, this.countdownLeft / TICK_RATE);
    v.raceTicks = this.phase === 'countdown' ? 0 : this.world.tick + alpha;
    v.standings = [...this.world.standings];

    for (let i = 0; i < v.ships.length; i++) {
      const sv = v.ships[i];
      const ship = this.world.ships[i];
      const prog = this.world.progress[i];
      sv.pos.copy(this.prevPos[i]).lerp(this.curPos[i], alpha);
      sv.quat.copy(this.prevQuat[i]).slerp(this.curQuat[i], alpha);
      sv.vel.copy(ship.vel);
      sv.speed = ship.speed();
      sv.grounded = ship.grounded;
      sv.boosting = ship.boosting;
      sv.drifting = ship.drifting;
      sv.respawning = ship.respawnTimer > 0;
      sv.finished = prog.finished;
      sv.eliminated = ship.eliminated;
      sv.inJump = ship.inJump;
      sv.wrongWay = prog.wrongWay;
      sv.boostEnergy = ship.boostEnergy;
      sv.wallContact = ship.wallContact;
      sv.steer = this.world.inputs[i].steer;
      sv.lap = prog.lap;
      sv.nextCheckpoint = prog.nextCheckpoint;
      sv.draftFactor = ship.draftFactor;
      sv.visible = !ship.eliminated || prog.finished;
    }

    // local HUD state
    const prog = this.world.progress[0];
    const ship = this.world.ships[0];
    v.local = {
      lap: Math.min(prog.lap, this.world.laps),
      totalLaps: this.world.laps,
      nextCheckpoint: prog.nextCheckpoint,
      checkpointCount: this.cfg.track.checkpoints.length,
      raceTimeTicks: this.phase === 'countdown' ? 0 : this.world.tick,
      currentLapTicks: this.phase === 'countdown' ? 0 : this.world.tick - prog.lapStartTick,
      bestLapTicks: prog.bestLapTicks,
      lastLapTicks: prog.lapTimes.length ? prog.lapTimes[prog.lapTimes.length - 1] : 0,
      splitDeltaMs: this.lastSplitDelta,
      wrongWay: prog.wrongWay,
      placement: this.world.standings.indexOf(0) + 1,
      finished: prog.finished,
      eliminated: ship.eliminated,
      finishTick: prog.finishTick,
      boostEnergy: ship.boostEnergy,
      speed: ship.speed(),
      lapFraction: prog.lapFraction(),
    };

    // elimination countdown
    if (this.world.mode === 'elimination' && this.phase === 'racing' && !this.world.raceOver) {
      v.eliminationIn = Math.max(0, (this.world.nextEliminationTick - this.world.tick) / TICK_RATE);
    } else {
      v.eliminationIn = null;
    }

    // ghost playback
    if (this.ghost && v.ghost) {
      const t = this.phase === 'racing' ? this.world.tick + alpha : 0;
      const fi = t / this.ghost.stride;
      const i0 = Math.floor(fi);
      const frac = fi - i0;
      const n = ghostFrameCount(this.ghost);
      if (i0 < n - 1) {
        readGhostFrame(this.ghost, i0, ghostPosA, ghostQuatA);
        readGhostFrame(this.ghost, i0 + 1, ghostPosB, ghostQuatB);
        v.ghost.pos.copy(ghostPosA).lerp(ghostPosB, frac);
        v.ghost.quat.copy(ghostQuatA).slerp(ghostQuatB, frac);
        v.ghost.visible = this.settings.showGhost && this.phase === 'racing';
      } else {
        v.ghost.visible = false;
      }
    }
  }

  /** After a time-trial PB, produce the recorded ghost for saving. */
  buildRecordedGhost(): GhostData | null {
    const prog = this.world.progress[0];
    if (!prog.finished || this.ghostSamples.length < 10) return null;
    return {
      version: GHOST_VERSION,
      trackId: this.cfg.trackId,
      trackSeed: this.cfg.trackSeed,
      vehicleId: this.cfg.playerVehicle,
      paintId: this.cfg.playerPaint,
      name: this.cfg.playerName || 'YOU',
      totalTicks: prog.finishTick,
      stride: 3,
      frames: encodeGhostFrames(this.ghostSamples),
    };
  }

  results() {
    return this.world.standings.map((shipIndex, place) => {
      const prog = this.world.progress[shipIndex];
      const info = this.view.ships[shipIndex].info;
      return {
        shipIndex,
        name: info.name,
        isBot: info.isBot,
        placement: place + 1,
        finishTick: prog.finishTick,
        bestLapTicks: prog.bestLapTicks,
        lapTicks: [...prog.lapTimes],
        dnf: !prog.finished,
        eliminated: this.world.ships[shipIndex].eliminated && !prog.finished,
      };
    });
  }

  isOver(): boolean {
    return this.phase === 'finished';
  }

  raceTimeString(): string {
    return ticksToTimeString(this.world.progress[0].finishTick);
  }

  dispose(): void {}
}

const ghostPosA = new Vec3();
const ghostPosB = new Vec3();
const ghostQuatA = new Quat();
const ghostQuatB = new Quat();
